import React from 'react';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import PDFUpload from './PDFUpload';

interface AuthGatedWorkflowProps {
    /**
     * Invoked when a visitor reaches for something gated and chooses to start a
     * trial. Wired in App.tsx to the existing TrialRegistrationDialog.
     *
     * Retained through the gate inversion because the prompt did not disappear
     * — it moved. See below.
     */
    onStartTrial: () => void;
    /** As above, for the unified sign-in dialog. */
    onOpenSignIn: () => void;
}

/**
 * The main workflow panel.
 *
 * <h2>What changed here, and why it is the whole feature</h2>
 * This component used to be the paywall:
 *
 * ```
 * return state.isAuthenticated ? <PDFUpload /> : <SignInPrompt … />;
 * ```
 *
 * One line, and an anonymous visitor never reached the product. They were asked
 * to create an account before seeing that it worked — the account wall at the
 * door, which is the thing ANONYMOUS_EVALUATION_MODE.md exists to remove.
 *
 * The governing principle (section 1): **free to build, pay when something
 * leaves the building.** Uploading a PDF, drawing fields and previewing the
 * result produce no artifact, so they sit in front of the wall. The three
 * downloads produce artifacts, so they sit behind it — and that is enforced on
 * the endpoints, not here.
 *
 * <h2>SignInPrompt was not deleted — it moved</h2>
 * It now appears at the moment a visitor reaches for something gated, naming
 * the specific thing they are about to receive (section 3.5.3). "Your completed
 * PDF is ready. Create an account to download it" converts; "sign in to
 * continue" on an empty page does not, because at that point they have nothing
 * of theirs to lose.
 *
 * <h2>Why the loading skeleton stays</h2>
 * Rendering the workflow before capabilities resolve would flash ungated
 * controls at a guest, then correct itself — which reads as the product taking
 * something away. Wait the few hundred milliseconds instead.
 */
const AuthGatedWorkflow: React.FC<AuthGatedWorkflowProps> = ({ onStartTrial, onOpenSignIn }) => {
    const { loading } = useCapabilities();

    if (loading) {
        return (
            <div className="bg-white/60 backdrop-blur-sm border border-neutral-200 rounded-2xl p-12 text-center">
                <div className="animate-pulse text-neutral-400">Preparing your workspace…</div>
            </div>
        );
    }

    // Everyone reaches the workflow. What they may TAKE from it is decided per
    // action, by capabilities, in the controls themselves.
    return <PDFUpload />;
};

export default AuthGatedWorkflow;
